import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowRight, ChevronDown, ChevronRight, CircleCheck, Clock3, Code2, Cpu, Database, Eye, FileText, Globe, HardDrive, House, KeyRound, Lock, Mail, MemoryStick, MonitorSmartphone,
  MoreVertical, Network, Play, Plus, ShieldCheck, Sparkles, Upload, UserRound, X, type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { CustomerActivityRecord, DomainRecord, HostingUsage, WebsiteScanSummary } from "@hostdock/types";
import { ActionMenu, AppLogo, cn, ErrorState, formatBytes, formatRelative, humanize, Skeleton, toast } from "@hostdock/ui";
import { api } from "../api";
import { threats, toReview } from "../lib/scan-result";
import { openCommandPalette } from "../layout";
import { useSession } from "../session";

export const ACTOR_LABEL: Record<CustomerActivityRecord["actor"], string> = { YOU: "You", HOSTDOCK_STAFF: "HostDock support", SYSTEM: "System" };

const MFA_DISMISS_KEY = "hostdock.home.mfa-banner-dismissed";
const readDismissed = () => { try { return sessionStorage.getItem(MFA_DISMISS_KEY) === "1"; } catch { return false; } };

/** Home dashboard. Every figure comes from the account's real data; where HostDock records nothing yet, it says so. */
export function HomePage() {
  const { session } = useSession();
  const home = useQuery({ queryKey: ["home"], queryFn: api.home });
  const usage = useQuery({ queryKey: ["hosting-usage"], queryFn: api.usage, refetchInterval: 60_000 });
  const sites = useQuery({ queryKey: ["websites"], queryFn: () => api.websites.list({ pageSize: 100 }), staleTime: 15_000 });
  const domains = useQuery({ queryKey: ["domains", "home"], queryFn: () => api.domains.list({ pageSize: 100 }), staleTime: 30_000 });
  const scans = useQuery({
    queryKey: ["security", "scans"], queryFn: api.security.scans,
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.latest && ["PENDING", "RUNNING"].includes(r.latest.status)) ? 3000 : 60_000),
  });
  const [dismissed, setDismissed] = useState(readDismissed);
  if (home.error) return <ErrorState error={home.error} onRetry={() => void home.refetch()} />;
  const d = home.data;
  const first = session.user.name.split(" ")[0] ?? session.user.name;

  return (
    <div className="flex flex-col">
      <Welcome name={first} usage={usage.data} status={d?.customer.status} />
      <ResourceLimitBanner usage={usage.data} className="mt-[8px]" />
      <BandwidthBanner usage={usage.data} className="mt-[8px]" />
      {d && !d.security.mfaEnabled && !dismissed && (
        <MfaBanner className="mt-[8px]" onDismiss={() => { setDismissed(true); try { sessionStorage.setItem(MFA_DISMISS_KEY, "1"); } catch { /* remembered for this page only */ } }} />
      )}
      <div className="mt-[9px] grid gap-[9px] xl:grid-cols-[minmax(0,1fr)_280px]">
        <QuickActions />
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
          <Shortcut to="/databases" icon={Database} title="Databases" description="MariaDB, PostgreSQL and more" />
          <Shortcut to="/developers" icon={Code2} title="API & webhooks" description="Tokens and event notifications" />
        </div>
      </div>
      <div className="mt-[14px] grid gap-[10px] xl:grid-cols-[minmax(0,1fr)_373px]">
        <WebsitesCard usage={usage} appTypes={new Map(sites.data?.items.map((s) => [s.id, s.appType]))} domains={domains.data?.items} />
        <PlanUsageCard usage={usage} />
      </div>
      <div className="mt-[12px] grid gap-[10px] xl:grid-cols-[minmax(0,1fr)_373px]">
        <PerformanceCard />
        <SecurityCenter home={d} domains={domains.data?.items} usage={usage.data} scans={scans.data?.items} />
      </div>
      <RecentActivity rows={d?.recentActivity} loading={home.isPending} />
    </div>
  );
}

// ---------- Welcome ----------

function Welcome({ name, usage, status }: { name: string; usage: HostingUsage | undefined; status: string | undefined }) {
  const plans = usage?.subscriptions.map((s) => s.plan.name) ?? [];
  const total = usage?.websites.length;
  const online = usage?.websites.filter((w) => w.status === "ACTIVE").length;
  const statusLabel = status ? status.charAt(0) + status.slice(1).toLowerCase() : "…";
  const allGood = status === "ACTIVE" && total != null && online === total;
  return (
    <section className="hd-card hd-welcome flex flex-col gap-5 px-4 pt-[13px] pb-[10px] lg:flex-row lg:items-center lg:justify-between lg:gap-4">
      <div className="flex min-w-0 items-center gap-[15px]">
        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center self-start rounded-full bg-[#d6f5fa] text-[#0a8ea0] max-sm:hidden lg:mt-[11px] dark:bg-accent-soft dark:text-accent">
          <House size={26} strokeWidth={1.7} />
        </span>
        <div className="min-w-0">
          <span className="inline-flex h-[19px] items-center gap-1.5 rounded-full bg-[#dff5f7] px-2.5 text-[10.5px] font-medium text-[#1f8796] dark:bg-accent-soft dark:text-accent">
            <Sparkles size={11} /> {plans.length ? `${plans.join(" · ")} plan` : "Your hosting account"}
          </span>
          <h1 className="mt-[8px] text-[24px] leading-[28px] font-bold tracking-[-0.015em] text-fg">Hello, {name} <span aria-hidden>👋</span></h1>
          <p className="mt-[2px] text-[13px] text-muted">Here's what's happening with your hosting account today.</p>
          <div className="mt-[12px] flex flex-wrap gap-[9px]">
            <WelcomeChip icon={Globe} label="Websites" value={total == null ? "…" : String(total)} />
            <WelcomeChip icon={CircleCheck} label="Online" value={online == null ? "…" : String(online)} tone="success" />
            <WelcomeChip icon={ShieldCheck} label="Account" value={statusLabel} tone={status === "ACTIVE" ? "success" : "neutral"} />
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-2 lg:mb-[19px] lg:flex lg:gap-0">
        <WelcomeStat className="lg:w-[130px] lg:pl-[23px]" icon={<Globe size={20} strokeWidth={1.7} className="text-[#0a8ea0] dark:text-accent" />} value={total == null ? "…" : String(total)} label="Websites" sub="Total websites" />
        <WelcomeStat className="lg:w-[126px] lg:pl-[15px]" icon={<span className="h-[14px] w-[14px] rounded-full bg-[#08c052]" />} value={online == null ? "…" : String(online)} label="Online" sub="Currently online" iconBg="bg-[#d4f5e1] dark:bg-success-soft" />
        <WelcomeStat className="lg:w-[137px] lg:pl-[18px]" icon={<UserRound size={20} strokeWidth={1.7} className="text-[#0a8ea0] dark:text-accent" />} value={statusLabel} label="Account" sub={allGood ? "All systems good" : status === "ACTIVE" ? "Some websites need attention" : "Contact support"} />
      </dl>
    </section>
  );
}

function WelcomeChip({ icon: Icon, label, value, tone = "accent" }: { icon: LucideIcon; label: string; value: string; tone?: "accent" | "success" | "neutral" }) {
  return (
    <span className={cn("inline-flex h-[22px] items-center gap-[9px] overflow-hidden rounded-md pl-2 text-[11px] text-[#425376] dark:text-muted", label === "Online" ? "bg-[#e3f8ec] pr-2.5 dark:bg-success-soft" : "bg-[#edf5f8] dark:bg-surface-3", label === "Websites" && "pr-2.5")}>
      <Icon size={14} className={tone === "success" ? "text-success" : "text-[#0a8ea0] dark:text-accent"} />
      <span className={label === "Online" ? "text-success" : undefined}>{label}</span>
      {label === "Account"
        ? <span className={cn("flex h-full items-center px-2.5", tone === "success" ? "bg-[#dcf7e7] text-success dark:bg-success-soft" : "bg-surface-3 text-fg")}>{value}</span>
        : <span className={cn("font-medium", tone === "success" ? "text-success" : "text-[#1b2559] dark:text-fg")}>{value}</span>}
    </span>
  );
}

function WelcomeStat({ icon, value, label, sub, iconBg = "bg-[#d3f1f5] dark:bg-accent-soft", className }: { icon: ReactNode; value: string; label: string; sub: string; iconBg?: string; className?: string }) {
  return (
    <div className={cn("flex min-w-0 items-center gap-[7px] rounded-lg bg-surface/60 p-2 lg:h-[64px] lg:shrink-0 lg:rounded-none lg:border-l lg:border-[#dfeaef] lg:bg-transparent dark:lg:border-line lg:py-0 lg:pr-0", className)}>
      <span className={cn("flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full max-sm:hidden", iconBg)}>{icon}</span>
      <div className="min-w-0 leading-tight">
        <dd className="text-[14px] font-bold text-fg">{value}</dd>
        <dt className="mt-[4px] text-[12px] text-[#1b2559] dark:text-fg">{label}</dt>
        <p className="mt-[4px] truncate text-[10px] text-subtle lg:overflow-visible lg:whitespace-nowrap">{sub}</p>
      </div>
    </div>
  );
}

function MfaBanner({ onDismiss, className }: { onDismiss(): void; className?: string }) {
  return (
    <div role="status" className={cn(className, "flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-[#f9dcc0] bg-[#fff5eb] px-[17px] py-[11px] dark:border-warning/30 dark:bg-warning-soft")}>
      <AlertTriangle size={24} strokeWidth={1.8} className="shrink-0 text-[#e0611f]" />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[12.5px] font-semibold text-fg">Enable two-factor authentication</p>
        <p className="mt-[4px] text-[11.5px] text-muted">Add an extra layer of security to protect your account and data.</p>
      </div>
      <Link to="/security" className="inline-flex h-8 items-center rounded-md bg-[#f15a15] px-4 text-[12.5px] font-semibold text-white shadow-[0_2px_6px_rgb(241_90_21/0.3)] transition hover:brightness-105">Enable now</Link>
      <button type="button" onClick={onDismiss} className="ml-1 rounded-md p-1 text-[#4d6a8a] hover:bg-[#fde6d2] dark:text-muted" aria-label="Dismiss for now"><X size={18} /></button>
    </div>
  );
}

const LIMIT_REASON: Record<string, string> = { memory: "memory (RAM)", cpu: "CPU", processes: "processes" };

/**
 * cPanel-style "resource limit reached" alert. Shown when a package pool has hit its hard CPU/RAM/process cap (reported
 * by the node and reconciled from heartbeats). While it is up, the affected websites serve a branded 508 page.
 */
function ResourceLimitBanner({ usage, className }: { usage: HostingUsage | undefined; className?: string }) {
  const limited = (usage?.subscriptions ?? []).filter((s) => s.resourceLimit);
  if (!limited.length) return null;
  const reasons = [...new Set(limited.map((s) => s.resourceLimit?.reason).filter(Boolean).map((r) => LIMIT_REASON[r!] ?? r!))];
  const plans = limited.map((s) => s.plan.name).join(", ");
  return (
    <div role="alert" className={cn(className, "flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-danger/30 bg-danger-soft px-[17px] py-[11px]")}>
      <AlertTriangle size={24} strokeWidth={1.8} className="shrink-0 text-danger" />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[12.5px] font-semibold text-fg">Resource limit reached{limited.length === 1 ? ` on your ${plans} plan` : ""}</p>
        <p className="mt-[4px] text-[11.5px] text-muted">
          {limited.length === 1 ? "This package" : `${limited.length} packages (${plans})`} hit the plan's hard limit
          {reasons.length ? ` for ${reasons.join(" and ")}` : ""}. Affected websites show a “resource limit reached” page until usage drops. Reduce load or upgrade for more resources.
        </p>
      </div>
      <Link to="/account" className="inline-flex h-8 items-center rounded-md bg-danger px-4 text-[12.5px] font-semibold text-white transition hover:brightness-105">View usage</Link>
    </div>
  );
}

/**
 * cPanel-style "bandwidth limit exceeded" alert. Shown when a package has used its plan's whole monthly bandwidth
 * allowance; while it is up, the affected websites serve a branded 509 page until the allowance resets next month.
 */
function BandwidthBanner({ usage, className }: { usage: HostingUsage | undefined; className?: string }) {
  const over = (usage?.subscriptions ?? []).filter((s) => s.bandwidthExceeded);
  if (!over.length) return null;
  const plans = over.map((s) => s.plan.name).join(", ");
  return (
    <div role="alert" className={cn(className, "flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-danger/30 bg-danger-soft px-[17px] py-[11px]")}>
      <AlertTriangle size={24} strokeWidth={1.8} className="shrink-0 text-danger" />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[12.5px] font-semibold text-fg">Bandwidth limit exceeded{over.length === 1 ? ` on your ${plans} plan` : ""}</p>
        <p className="mt-[4px] text-[11.5px] text-muted">
          {over.length === 1 ? "This package" : `${over.length} packages (${plans})`} used all the bandwidth included this month. Affected websites show a “bandwidth limit exceeded” page until the allowance resets at the start of next month. Upgrade for more bandwidth.
        </p>
      </div>
      <Link to="/account" className="inline-flex h-8 items-center rounded-md bg-danger px-4 text-[12.5px] font-semibold text-white transition hover:brightness-105">View usage</Link>
    </div>
  );
}

// ---------- Quick actions ----------

const QUICK: { to: string; icon: LucideIcon; iconClass: string; title: string; description: string }[] = [
  { to: "/websites/new", icon: Plus, iconClass: "bg-[#e2f8fa] text-[#0a9fb1]", title: "Create a website", description: "WordPress, PHP, Node.js or Python" },
  { to: "/migrate/new", icon: Upload, iconClass: "bg-[#e3f7fa] text-[#0a9fb1]", title: "Migrate a website", description: "Move a site from another host" },
  { to: "/email", icon: Mail, iconClass: "bg-[#e6faee] text-[#17a352]", title: "Set up email", description: "Mailboxes and aliases on your domain" },
  { to: "/dns", icon: Network, iconClass: "bg-[#fdf3e8] text-[#e0611f]", title: "Zone Editor", description: "DNS records of your domains" },
];

function QuickActions() {
  return (
    <section className="hd-card px-[9px] pt-[9px] pb-[8px]" aria-labelledby="quick-actions">
      <div className="mb-[8px] flex h-[20px] items-center justify-between px-[4px]">
        <h2 id="quick-actions" className="text-[13.5px] font-semibold text-fg">Quick actions</h2>
        <button type="button" onClick={openCommandPalette} className="inline-flex items-center gap-1 text-[10.5px] font-medium text-accent hover:underline">View all tools <ArrowRight size={13} /></button>
      </div>
      <div className="grid grid-cols-1 gap-[9px] sm:grid-cols-2 lg:grid-cols-[196fr_180fr_177fr_189fr]">
        {QUICK.map((a) => (
          <Link key={a.to} to={a.to} className="group flex h-[69px] items-center gap-[10px] rounded-lg border border-line bg-surface pr-[8px] pl-[8px] transition-colors hover:border-line-strong hover:bg-surface-2/60">
            <span className={cn("flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-lg dark:bg-surface-3", a.iconClass)}><a.icon size={27} strokeWidth={1.5} /></span>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-[11.5px] font-semibold text-fg">{a.title}</span>
              <span className="mt-[4px] line-clamp-2 block text-[9.5px] leading-[13px] tracking-[-0.005em] text-subtle">{a.description}</span>
            </span>
            <ChevronRight size={14} className="-ml-[8px] shrink-0 text-fg/80 transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function Shortcut({ to, icon: Icon, title, description }: { to: string; icon: LucideIcon; title: string; description: string }) {
  return (
    <Link to={to} className="hd-card group flex h-[54px] items-center gap-3 px-[10px] transition-colors hover:border-line-strong">
      <span className="flex h-[40px] w-[42px] shrink-0 items-center justify-center rounded-lg bg-[#e5f6f9] text-[#0a8ea0] dark:bg-accent-soft dark:text-accent"><Icon size={23} strokeWidth={1.5} /></span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-[11.5px] font-semibold text-fg">{title}</span>
        <span className="mt-[3px] block truncate text-[10px] text-subtle">{description}</span>
      </span>
      <ChevronRight size={17} className="mr-[8px] shrink-0 text-fg/70 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

// ---------- Websites and plan usage ----------

type UsageQuery = { data?: HostingUsage; error: unknown; refetch: () => unknown };

/** Primary-domain certificate state, from the domains API. */
function sslOf(domains: DomainRecord[] | undefined, websiteId: string, name: string) {
  const d = domains?.find((x) => x.websiteId === websiteId && x.name === name) ?? domains?.find((x) => x.websiteId === websiteId && x.kind === "PRIMARY");
  const s = d?.certificate?.status;
  if (!domains) return null;
  if (s === "ISSUED") return { label: "SSL active", tone: "ok" as const };
  if (s === "FAILED") return { label: "SSL failed", tone: "bad" as const };
  if (s) return { label: "SSL pending", tone: "wait" as const };
  return { label: "No SSL yet", tone: "wait" as const };
}

const LOGO_RING: Record<string, string> = { NODE: "bg-[#d5f7e1]", WORDPRESS: "bg-[#e6eefa]", WOOCOMMERCE: "bg-[#f0e8fb]", PHP: "bg-[#e8e9f7]", PYTHON: "bg-[#e5eef8]", STATIC: "bg-[#fbe4f1]" };

function WebsitesCard({ usage, appTypes, domains }: { usage: UsageQuery; appTypes: Map<string, string>; domains: DomainRecord[] | undefined }) {
  const navigate = useNavigate();
  const d = usage.data;
  return (
    <section className="hd-card flex min-w-0 flex-col px-[12px] pt-[6px] pb-[12px]" aria-labelledby="your-websites">
      <div className="flex items-start justify-between gap-3 pl-[2px]">
        <div className="pt-[4px] leading-tight">
          <h2 id="your-websites" className="text-[13.5px] font-semibold text-fg">Your websites</h2>
          <p className="mt-[4px] text-[12px] text-muted">Live status and measured resource use.</p>
        </div>
        <Link to="/websites/new" className="inline-flex h-8 w-[118px] shrink-0 items-center justify-center rounded-md bg-[#029bb0] text-[12.5px] font-semibold text-white shadow-[0_2px_8px_rgb(2_155_176/0.3)] transition hover:bg-[#028a9d]">New Website</Link>
      </div>
      <div className="mt-[2px] min-w-0">
        {usage.error ? (
          <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
        ) : !d ? (
          <div className="flex flex-col gap-2">{[0, 1].map((i) => <Skeleton key={i} className="h-[83px] w-full rounded-lg" />)}</div>
        ) : d.websites.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line-strong px-4 py-10 text-center">
            <p className="text-[13px] font-semibold">No websites yet</p>
            <p className="mt-1 text-[12px] text-muted">Launch WordPress or your own app with “New Website”.</p>
          </div>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {d.websites.map((w) => {
              const app = appTypes.get(w.id) ?? "";
              const ssl = sslOf(domains, w.id, w.primaryDomain);
              const online = w.status === "ACTIVE";
              return (
                <li key={w.id} className="relative flex flex-wrap items-center gap-x-[14px] gap-y-3 py-[12px] pr-[12px] pl-[10px] sm:flex-nowrap">
                  <span className={cn("flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full dark:bg-surface-3", LOGO_RING[app] ?? LOGO_RING[w.runtime.toUpperCase()] ?? "bg-[#e3f5f8]")}>
                    <AppLogo appType={app} runtime={w.runtime} size="xl" className="!bg-transparent !ring-0" />
                  </span>
                  <div className="min-w-0 flex-[1_1_12rem] leading-tight">
                    <Link to={`/websites/${w.id}`} className="block truncate text-[13px] font-bold text-fg hover:text-accent">{w.primaryDomain}</Link>
                    <Link to={`/websites/${w.id}`} className="mt-[5px] inline-flex items-center gap-1 text-fg/70">
                      <span className={cn("inline-flex h-[20px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium", online ? "bg-[#d9f8e6] text-[#14a04a] dark:bg-success-soft" : "bg-warning-soft text-warning")}>
                        <span className={cn("h-[7px] w-[7px] rounded-full", online ? "bg-[#07aa41]" : "bg-warning")} />{humanize(w.status)}
                      </span>
                      <ChevronRight size={14} />
                    </Link>
                    <p className="mt-[6px] flex min-w-0 items-center gap-1.5 text-[9.5px] text-subtle">
                      <Lock size={10} className="shrink-0" /><span className="truncate">{w.primaryDomain}</span>
                      {ssl && <><span className="mx-1 h-2.5 w-px bg-line-strong" aria-hidden /><span className={cn("inline-flex items-center gap-1 whitespace-nowrap", ssl.tone === "ok" ? "text-[#14a04a]" : ssl.tone === "bad" ? "text-danger" : "text-warning")}><span className="h-[6px] w-[6px] rounded-full bg-current" />{ssl.label}</span></>}
                    </p>
                  </div>
                  <div className="grid w-full grid-cols-3 gap-3 sm:w-auto sm:grid-cols-[93px_117px_60px] sm:gap-0">
                    <Measure icon={HardDrive} label="Disk" value={w.diskBytes != null ? formatBytes(w.diskBytes) : "—"} />
                    <Measure icon={MemoryStick} label="Memory" value={w.memoryBytes != null ? `${formatBytes(w.memoryBytes)} / ${formatBytes(w.memoryLimitBytes)}` : "—"} />
                    <Measure icon={Cpu} label="CPU" value={w.cpuPercent != null ? `${w.cpuPercent}%` : "—"} />
                  </div>
                  <div className="max-sm:absolute max-sm:top-3 max-sm:right-2 sm:ml-[18px]">
                    <ActionMenu
                      label={`Actions for ${w.primaryDomain}`}
                      trigger={<button type="button" aria-label={`Actions for ${w.primaryDomain}`} className="inline-flex h-8 w-6 items-center justify-center rounded-md text-fg hover:bg-surface-2"><MoreVertical size={18} /></button>}
                      actions={[
                        { label: "Open website", onSelect: () => navigate(`/websites/${w.id}`) },
                        { label: "File manager", onSelect: () => navigate(`/websites/${w.id}?tab=files`) },
                        { label: "Domains", onSelect: () => navigate(`/websites/${w.id}?tab=domains`) },
                        { label: "Databases", onSelect: () => navigate(`/websites/${w.id}?tab=databases`) },
                        { label: "Security", onSelect: () => navigate(`/websites/${w.id}?tab=security`) },
                        { label: "Logs", onSelect: () => navigate(`/websites/${w.id}?tab=logs`) },
                      ]}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function Measure({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <span className="flex min-w-0 flex-col gap-[9px] leading-none">
      <span className="inline-flex items-center gap-1.5 text-[10px] text-[#9aa1bd]"><Icon size={12} />{label}</span>
      <span className="truncate text-[14px] font-semibold text-[#0b1035] tabular-nums dark:text-fg">{value}</span>
    </span>
  );
}

function Ring({ label, pct, full }: { label: string; pct: number | null; full?: boolean }) {
  const size = 46;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }} role="meter" aria-label={label} aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--hd-surface-3)" strokeWidth={stroke} />
        {p > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={full ? "#1d7a69" : "#13a9b9"} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - p / 100)} />}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10.5px] font-bold text-fg tabular-nums">{pct == null ? "—" : `${Math.round(p)}%`}</span>
    </span>
  );
}

function PlanUsageCard({ usage }: { usage: UsageQuery }) {
  const d = usage.data;
  const s = d?.subscriptions[0];
  const rows = s ? [
    { label: "Websites", used: s.usage.websites, limit: s.limits.websites, text: `${s.usage.websites} of ${s.limits.websites}`, to: "/websites" },
    { label: "Storage", used: s.usage.storageBytes, limit: s.limits.storageGb * 1024 ** 3, text: s.usage.storageBytes == null ? "Not measured yet" : `${formatBytes(s.usage.storageBytes)} of ${formatBytes(s.limits.storageGb * 1024 ** 3)}`, to: "/websites" },
    // Bandwidth resets monthly; a plan with 0 GB means unlimited, so no ring, just the amount used.
    { label: "Bandwidth", used: s.usage.bandwidthBytes, limit: s.limits.bandwidthGb * 1024 ** 3, text: s.limits.bandwidthGb > 0 ? `${formatBytes(s.usage.bandwidthBytes)} of ${formatBytes(s.limits.bandwidthGb * 1024 ** 3)} this month` : `${formatBytes(s.usage.bandwidthBytes)} this month (unlimited)`, to: "/websites" },
    { label: "Databases", used: s.usage.databases, limit: s.limits.databases, text: `${s.usage.databases} of ${s.limits.databases}`, to: "/databases" },
    { label: "Cron jobs", used: s.usage.cronJobs, limit: s.limits.cronJobs, text: `${s.usage.cronJobs} of ${s.limits.cronJobs}`, to: "/websites" },
  ] : [];
  return (
    <section className="hd-card flex flex-col px-[12px] pt-[6px] pb-[1px]" aria-labelledby="plan-usage">
      <div className="flex items-center justify-between gap-2 pl-[1px]">
        <h2 id="plan-usage" className="pt-[2px] text-[13.5px] font-semibold text-fg">Plan usage</h2>
        {s && <span className="inline-flex h-5 items-center gap-1.5 rounded-md bg-[#d9f2f4] px-2 text-[10.5px] font-medium text-[#1f8796] dark:bg-accent-soft dark:text-accent"><Sparkles size={11} />{s.plan.name} plan</span>}
      </div>
      {usage.error ? <ErrorState error={usage.error} onRetry={() => void usage.refetch()} /> : !d ? (
        <Skeleton className="mt-3 h-52 w-full rounded-lg" />
      ) : !s ? (
        <p className="mt-3 pb-4 text-[12.5px] text-muted">You don't have a hosting package yet. Once one is active, its limits and usage appear here.</p>
      ) : (
        <ul className="mt-[7px] flex flex-col divide-y divide-line rounded-lg border border-line">
          {rows.map((r) => {
            const pct = r.used == null || r.limit <= 0 ? null : (r.used / r.limit) * 100;
            return (
              <li key={r.label}>
                <Link to={r.to} className="group flex h-[52px] items-center gap-[14px] pr-[16px] pl-[4px] transition-colors hover:bg-surface-2/70">
                  <Ring label={r.label} pct={pct} full={pct != null && pct >= 100} />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block text-[12px] font-semibold text-fg">{r.label}</span>
                    <span className="mt-[4px] block truncate text-[11px] text-[#8b8ca2] tabular-nums">{r.text}</span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-fg/70 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </li>
            );
          })}
          {d.subscriptions.length > 1 && <li className="px-1 pb-2 text-[11px] text-subtle">You have {d.subscriptions.length} packages; this shows {s.plan.name}. See Account for the others.</li>}
        </ul>
      )}
    </section>
  );
}

// ---------- Performance ----------

const DAY = 86_400_000;
const shortDate = (t: number) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/**
 * Traffic analytics (visits, bandwidth, response time, uptime history) are not recorded by HostDock yet. The card keeps
 * its place and axes but shows no figures and says so plainly, instead of drawing invented numbers.
 */
function PerformanceCard() {
  const today = new Date().setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, i) => today - (6 - i) * DAY);
  return (
    <section className="hd-card flex min-w-0 flex-col px-[12px] pt-[9px] pb-[12px]" aria-labelledby="perf">
      <div className="flex flex-wrap items-start justify-between gap-3 pl-[2px]">
        <div className="pt-[7px] leading-tight">
          <h2 id="perf" className="text-[13.5px] font-semibold text-fg">Website performance</h2>
          <p className="mt-[4px] text-[11.5px] text-muted">Last 7 days for all websites</p>
        </div>
        <div className="flex gap-[10px]" title="Available once HostDock records traffic for your websites">
          <FakeSelect label="Last 7 days" className="w-[84px]" />
          <FakeSelect label="All websites" className="w-[111px]" />
        </div>
      </div>
      <div className="relative mt-[6px] h-[168px] text-[8.5px] text-subtle" role="img" aria-label="Visits and response time chart: no data recorded yet">
        <div className="absolute top-[10px] left-[14px] flex items-center gap-[14px]">
          <span className="inline-flex items-center gap-[6px]"><span className="h-[6px] w-[6px] rounded-full bg-[#7fdde2]" />Visits</span>
          <span className="inline-flex items-center gap-[6px]"><span className="h-[6px] w-[6px] rounded-full bg-[#0aa3b4]" />Response time (ms)</span>
        </div>
        <div className="absolute top-[34px] right-[50px] bottom-[29px] left-[33px]">
          {[4, 3, 2, 1, 0].map((i) => (
            // Grid lines only: without recorded data there is no scale to show.
            <div key={i} className="absolute inset-x-0 border-t border-line" style={{ top: `${((4 - i) / 4) * 100}%`, borderTopStyle: i === 0 ? "solid" : "dashed" }} />
          ))}
          <div className="absolute inset-x-0 top-full mt-[9px] flex justify-between">
            {days.map((t) => <span key={t} className="w-0 text-center whitespace-nowrap"><span className="inline-block -translate-x-1/2">{shortDate(t)}</span></span>)}
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center px-6 pt-4">
          <p className="max-w-sm rounded-lg bg-surface/90 px-3 py-2 text-center text-[11.5px] leading-snug text-muted shadow-xs">
            <span className="block font-semibold text-fg">No traffic data yet</span>
            HostDock does not record visits or response times for websites yet, so nothing is charted here.
          </p>
        </div>
      </div>
      <div className="mt-[9px] grid grid-cols-2 gap-[9px] lg:grid-cols-4">
        <PerfTile icon={Eye} label="Total visits" />
        <PerfTile icon={MonitorSmartphone} label="Total bandwidth" />
        <PerfTile icon={Clock3} label="Avg. response time" />
        <PerfTile icon={FileText} label="Uptime" />
      </div>
    </section>
  );
}

function FakeSelect({ label, className }: { label: string; className?: string }) {
  return (
    <button type="button" disabled className={cn("inline-flex h-7 cursor-not-allowed items-center justify-between gap-1 rounded-md border border-line bg-surface px-[9px] text-[10.5px] whitespace-nowrap text-fg/80", className)}>
      {label}<ChevronDown size={13} />
    </button>
  );
}

function PerfTile({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex h-[70px] items-center gap-3 rounded-lg border border-line px-[9px]">
      <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[#dff5f8] text-[#0a8ea0] dark:bg-accent-soft dark:text-accent"><Icon size={20} strokeWidth={1.7} /></span>
      <div className="min-w-0 leading-tight">
        <p className="text-[14px] font-bold text-fg">—</p>
        <p className="mt-[4px] truncate text-[11px] text-subtle">{label}</p>
        <p className="mt-[5px] text-[10px] text-subtle">Not recorded yet</p>
      </div>
    </div>
  );
}

// ---------- Security center ----------

type Badge = { label: string; tone: "ok" | "warn" | "bad" | "off" };
const BADGE_TONE = { ok: "bg-[#d9f8e6] text-[#14a04a] dark:bg-success-soft", warn: "bg-[#fdeedd] text-[#e0611f] dark:bg-warning-soft", bad: "bg-danger-soft text-danger", off: "bg-surface-3 text-muted" };

function SecurityCenter({ home, domains, usage, scans }: {
  home: Awaited<ReturnType<typeof api.home>> | undefined;
  domains: DomainRecord[] | undefined;
  usage: HostingUsage | undefined;
  scans: WebsiteScanSummary[] | undefined;
}) {
  const qc = useQueryClient();
  const scannable = (scans ?? []).filter((s) => s.scanAvailable && !(s.latest && ["PENDING", "RUNNING"].includes(s.latest.status)));
  const runScan = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(scannable.map((s) => api.security.scan(s.websiteId)));
      return { started: results.filter((r) => r.status === "fulfilled").length, failed: results.filter((r) => r.status === "rejected").length };
    },
    onSuccess: (r) => {
      if (r.started) toast.success(`Malware scan started for ${r.started} website${r.started === 1 ? "" : "s"}`);
      if (r.failed) toast.error(`${r.failed} scan${r.failed === 1 ? "" : "s"} could not start`);
      void qc.invalidateQueries({ queryKey: ["security", "scans"] });
    },
  });

  const done = (scans ?? []).filter((s) => s.latest?.status === "COMPLETED" || s.latest?.status === "PARTIAL");
  const infected = done.reduce((n, s) => n + threats(s.latest), 0);
  const review = done.reduce((n, s) => n + toReview(s.latest), 0);
  const running = (scans ?? []).some((s) => s.latest && ["PENDING", "RUNNING"].includes(s.latest.status));
  const lastAt = done.map((s) => s.latest!.completedAt ?? s.latest!.createdAt).sort().at(-1);
  const anyScanner = (scans ?? []).some((s) => s.scanAvailable);
  const malware: { detail: string; badge: Badge } = !scans
    ? { detail: "Checking…", badge: { label: "…", tone: "off" } }
    : running ? { detail: "Scan in progress", badge: { label: "Scanning", tone: "warn" } }
      : infected ? { detail: `Last scan: ${formatRelative(lastAt!)}`, badge: { label: `${infected} threat${infected === 1 ? "" : "s"}`, tone: "bad" } }
        : review ? { detail: `Last scan: ${formatRelative(lastAt!)}`, badge: { label: `${review} to review`, tone: "warn" } }
        : lastAt ? { detail: `Last scan: ${formatRelative(lastAt)}`, badge: { label: "No issues", tone: "ok" } }
          : anyScanner ? { detail: "No scans yet", badge: { label: "Not scanned", tone: "off" } }
            : { detail: "Not set up on your server yet", badge: { label: "Unavailable", tone: "off" } };

  const primary = (usage?.websites ?? []).map((w) => domains?.find((x) => x.websiteId === w.id && x.name === w.primaryDomain));
  const issued = primary.filter((x) => x?.certificate?.status === "ISSUED").length;
  const ssl: { detail: string; badge: Badge } = !usage || !domains
    ? { detail: "Checking…", badge: { label: "…", tone: "off" } }
    : primary.length === 0 ? { detail: "No websites yet", badge: { label: "None", tone: "off" } }
      : issued === primary.length ? { detail: "All websites protected", badge: { label: "Active", tone: "ok" } }
        : { detail: `${issued} of ${primary.length} websites protected`, badge: { label: "Pending", tone: "warn" } };

  const healthy = home && home.customer.status === "ACTIVE" && infected === 0 && (primary.length === 0 || issued === primary.length);
  return (
    <section className="hd-card flex flex-col px-[11px] pt-[4px] pb-[6px]" aria-labelledby="security-center">
      <div className="flex h-[22px] items-center justify-between pl-[2px]">
        <h2 id="security-center" className="text-[13.5px] font-semibold text-fg">Security center</h2>
        {home && <StatusPill badge={healthy ? { label: "Healthy", tone: "ok" } : { label: "Needs attention", tone: "warn" }} />}
      </div>
      {!home ? <Skeleton className="mt-3 h-72 w-full rounded-lg" /> : (
        <>
          <ul className="mt-[3px] rounded-lg border border-line">
            <SecRow icon={AlertTriangle} iconClass={home.security.mfaEnabled ? "text-[#14a04a]" : "text-[#e0611f]"} tileClass={home.security.mfaEnabled ? undefined : "bg-[#fdeee3] dark:bg-warning-soft"} title="Two-factor authentication" detail={home.security.mfaEnabled ? "Enabled on your account" : "Not enabled yet"} to="/security" trailing={<StatusPill badge={home.security.mfaEnabled ? { label: "On", tone: "ok" } : { label: "Off", tone: "warn" }} />} />
            <SecRow icon={ShieldCheck} title="Signed-in sessions" detail="Review where you're signed in" to="/security" trailing={<span className="pr-1 text-[12px] font-semibold tabular-nums">{home.security.activeSessions}</span>} />
            <SecRow icon={KeyRound} title="Password" detail="Change your sign-in password" to="/security" />
            <SecRow icon={UserRound} title="Account status" detail="Your hosting account" to="/account" trailing={<StatusPill badge={{ label: humanize(home.customer.status), tone: home.customer.status === "ACTIVE" ? "ok" : "warn" }} />} />
          </ul>
          <ul className="mt-[8px] rounded-lg border border-line">
            <SecRow icon={ShieldCheck} title="Malware scan" detail={malware.detail} to="/security" trailing={<StatusPill badge={malware.badge} />} />
            <SecRow icon={ShieldCheck} title="Web application firewall" detail="Not offered by HostDock yet" trailing={<StatusPill badge={{ label: "Not available", tone: "off" }} />} />
            <SecRow icon={Lock} title="SSL certificates" detail={ssl.detail} to="/domains" trailing={<StatusPill badge={ssl.badge} />} />
          </ul>
          <button
            type="button"
            onClick={() => runScan.mutate()}
            disabled={!scannable.length || runScan.isPending}
            title={!anyScanner ? "Malware scanning is not set up on your server yet" : running && !scannable.length ? "A scan is already running" : undefined}
            className="mt-[5px] flex h-[26px] items-center justify-center gap-2 rounded-md bg-[#daf2f7] text-[11.5px] font-medium text-[#0b7f91] transition-colors hover:bg-[#c9ecf3] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-accent-soft dark:text-accent"
          >
            <Play size={12} fill="currentColor" />{runScan.isPending ? "Starting…" : "Run scan"}
          </button>
        </>
      )}
    </section>
  );
}

function StatusPill({ badge }: { badge: Badge }) {
  return (
    <span className={cn("inline-flex h-[18px] items-center gap-1.5 rounded-full px-2 text-[10px] font-medium whitespace-nowrap", BADGE_TONE[badge.tone])}>
      {badge.tone !== "off" && <span className="h-[6px] w-[6px] rounded-full bg-current" />}{badge.label}
    </span>
  );
}

function SecRow({ icon: Icon, iconClass, tileClass, title, detail, to, trailing }: { icon: LucideIcon; iconClass?: string; tileClass?: string; title: string; detail: string; to?: string; trailing?: ReactNode }) {
  const inner = (
    <>
      <span className={cn("ml-[12px] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md", tileClass ?? "bg-[#e5f8ed] dark:bg-success-soft")}>
        <Icon size={18} strokeWidth={1.7} className={iconClass ?? "text-[#14a04a]"} />
      </span>
      <span className="ml-[15px] min-w-0 flex-1 leading-tight">
        <span className="block text-[11px] font-semibold text-fg">{title}</span>
        <span className="mt-[3px] block truncate text-[9.5px] text-[#7f90a1]">{detail}</span>
      </span>
      {trailing && <span className="ml-2 shrink-0">{trailing}</span>}
      <ChevronRight size={15} className={cn("mr-[9px] ml-[14px] shrink-0", to ? "text-fg/70" : "invisible")} />
    </>
  );
  return (
    <li className="border-b border-line last:border-0">
      {to ? <Link to={to} className="flex h-[38.5px] items-center transition-colors hover:bg-surface-2/70">{inner}</Link> : <div className="flex h-[38.5px] items-center">{inner}</div>}
    </li>
  );
}

// ---------- Recent activity ----------

function RecentActivity({ rows, loading }: { rows: CustomerActivityRecord[] | undefined; loading: boolean }) {
  const cols = "grid grid-cols-[1fr_auto] gap-x-3 md:grid-cols-[1.2fr_2fr_0.8fr_1.4fr_1fr_16px] xl:grid-cols-[179px_305px_117px_211px_minmax(0,1fr)_16px] xl:gap-x-0";
  return (
    <section className="hd-card mt-[12px] px-[12px] pt-[5px] pb-[8px]" aria-labelledby="recent-activity">
      <div className="flex items-start justify-between gap-3 pl-[2px]">
        <div className="leading-tight">
          <h2 id="recent-activity" className="text-[13px] font-semibold text-fg">Recent activity</h2>
          <p className="mt-[3px] text-[11px] text-subtle">Sign-ins and changes on your account and websites.</p>
        </div>
        <Link to="/security" className="mt-[2px] inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-accent hover:underline">View all activity <ArrowRight size={13} /></Link>
      </div>
      <div className="mt-[6px] overflow-hidden rounded-md bg-[#f6fafc] dark:bg-surface-2/50">
        <div className={cn(cols, "hidden h-[13px] items-center bg-[#e2f1f6] px-[16px] text-[7.5px] font-medium tracking-wide text-subtle uppercase md:grid dark:bg-surface-2")} role="row">
          <span>When</span><span>Activity</span><span>By</span><span>IP address</span><span>Result</span><span />
        </div>
        {loading ? <Skeleton className="mt-1 h-24 w-full" /> : !rows?.length ? (
          <p className="py-6 text-center text-[12px] text-muted">No activity yet. Account and website events will appear here.</p>
        ) : (
          <ul>
            {rows.slice(0, 8).map((r) => (
              <li key={r.id}>
                <Link to="/security" className={cn(cols, "items-center px-[16px] py-1.5 text-[11px] text-[#5d6c80] transition-colors hover:bg-surface-3/60 md:h-[13px] md:py-0 md:text-[9.5px] dark:text-muted")}>
                  <span className="text-subtle max-md:order-2 max-md:text-right">{formatRelative(r.occurredAt)}</span>
                  <span className="truncate text-[#5d6c80] max-md:order-1 max-md:font-medium max-md:text-fg dark:text-muted">{humanize(r.action)}</span>
                  <span className="hidden md:inline">{ACTOR_LABEL[r.actor]}</span>
                  <span className="hidden truncate tabular-nums md:inline">{r.ip ?? "—"}</span>
                  <span className="hidden md:inline"><ResultPill result={r.result} /></span>
                  <ChevronRight size={10} className="hidden text-fg/60 md:block" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ResultPill({ result }: { result: string }) {
  const ok = result === "SUCCESS";
  return (
    <span className={cn("inline-flex h-[11px] items-center gap-1 rounded-full px-1.5 text-[8.5px] font-medium", ok ? "bg-[#daffe6] text-[#14a04a] dark:bg-success-soft" : "bg-danger-soft text-danger")}>
      <span className="h-[5px] w-[5px] rounded-full bg-current" />{humanize(result)}
    </span>
  );
}
